import styled, { css } from 'styled-components';

import { Chip } from '../index.tsx';

type S27ChipColor =
  | 'default'
  | 'outline'
  | 'upcoming'
  | 'live'
  | 'yellow'
  | 'orange'
  | 'red'
  | 'blue'
  | 'green'
  | 'purple'
  | 'teal'
  | 'fire';

type StyleProps = {
  color?: S27ChipColor;
  size?: 'small' | 'compact';
};

const wash = (accent: string, base = 'var(--c-dark-primary)') =>
  `color-mix(in srgb, ${accent} 16%, ${base})`;

export const S27Chip = styled(Chip)<StyleProps>`
  box-sizing: border-box;
  border-radius: 100px;
  background: var(--chip-surface-color);
  backdrop-filter: none;
  /* The glow is listed first so it paints over the ring, as the design's inner shadow does. */
  box-shadow:
    inset 0 0 9px 0 var(--chip-glow-color),
    inset 0 0 0 1px var(--chip-border-color);
  font: var(--t-brand-xxs);
  color: var(--c-white);
  text-transform: uppercase;

  ${({ color = 'default' }) => {
    switch (color) {
      case 'outline':
        return css`
          --chip-border-color: rgba(var(--c-rgb-white), 0.6);
          --chip-surface-color: var(--c-nd-100-opaque);
          --chip-glow-color: var(--c-black);
        `;
      case 'upcoming':
        return css`
          --chip-border-color: var(--c-limited-tertiary);
          --chip-surface-color: ${wash(
            'var(--c-yellow-300)',
            'var(--c-limited-tertiary)'
          )};
          --chip-glow-color: var(--c-limited);
        `;
      case 'live':
        return css`
          --chip-border-color: var(--c-rare-tertiary);
          --chip-surface-color: ${wash(
            'var(--c-red-300)',
            'var(--c-rare-tertiary)'
          )};
          --chip-glow-color: var(--c-rare);
        `;
      case 'yellow':
        return css`
          --chip-border-color: var(--c-gold-400);
          --chip-surface-color: ${wash('var(--c-gold-100)')};
          --chip-glow-color: var(--c-gold-300);
        `;
      case 'orange':
        return css`
          --chip-border-color: var(--c-gold-200);
          --chip-surface-color: ${wash('var(--c-gold-200)')};
          --chip-glow-color: var(--c-rewards-orange);
        `;
      case 'red':
        return css`
          --chip-border-color: var(--c-competition-laliga-tertiary);
          --chip-surface-color: ${wash('var(--c-modes-pro-tertiary)')};
          --chip-glow-color: var(--c-rare);
        `;
      case 'blue':
        return css`
          --chip-border-color: var(--c-competition-mls-tertiary);
          --chip-surface-color: ${wash('var(--c-superRare)')};
          --chip-glow-color: var(--c-superRare);
        `;
      case 'green':
        return css`
          --chip-border-color: var(--c-competition-allStar-tertiary);
          --chip-surface-color: ${wash('var(--c-green-400)')};
          --chip-glow-color: var(--c-modes-classic-arena-secondary);
        `;
      case 'purple':
        return css`
          --chip-border-color: var(--c-competition-pl-tertiary);
          --chip-surface-color: ${wash('var(--c-modes-s27-arena-secondary)')};
          --chip-glow-color: var(--c-modes-s27-arena-secondary);
        `;
      case 'teal':
        return css`
          --chip-border-color: var(--c-competition-allStar-tertiary);
          --chip-surface-color: ${wash('var(--c-competition-allStar-primary)')};
          --chip-glow-color: var(--c-competition-allStar-primary);
        `;
      case 'fire':
        return css`
          --chip-border-color: var(--c-modes-pro-tertiary);
          --chip-surface-color: ${wash('var(--c-modes-pro-primary)')};
          --chip-glow-color: var(--c-modes-pro-secondary);
        `;
      case 'default':
      default:
        return css`
          --chip-border-color: var(--c-common-tertiary);
          --chip-surface-color: var(--c-nd-100-opaque);
          --chip-glow-color: var(--c-nd-150);
          backdrop-filter: blur(8px);
        `;
    }
  }}
  ${({ size = 'small' }) =>
    size === 'compact'
      ? css`
          min-height: 20px;
          padding: 0 6px;
        `
      : css`
          min-height: 24px;
          padding: 0 var(--unit);
        `}
`;
